import { useEffect, useRef } from "react";

function getDocumentHeight(): number {
  const root = document.getElementById("root");
  const body = document.body;

  const rootHeight = root?.getBoundingClientRect().height ?? 0;
  const bodyHeight = body?.getBoundingClientRect().height ?? 0;
  const height = Math.max(rootHeight, bodyHeight);
  return Math.ceil(height || window.innerHeight);
}

function useAutoResize(): void {
  const lastSentHeightRef = useRef<number>(-1);

  useEffect(() => {
    let mutationObserver: MutationObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let rafId: number | null = null;

    const postHeight = (): void => {
      const height = getDocumentHeight();
      if (height !== lastSentHeightRef.current) {
        lastSentHeightRef.current = height;

        console.debug("[useAutoResize] posting resize height:", height, "px");
        window.parent.postMessage({ type: "resize", height }, "*");
      }
    };

    const postHeightRaf = (): void => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        postHeight();
      });
    };

    // [KS] Initial broadcast after first layout load
    postHeightRaf();

    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(() => postHeightRaf());
      resizeObserver.observe(document.documentElement);
      resizeObserver.observe(document.body);
    }

    // [KS] MutationObserver as an additional fallback (captures DOM changes in case ResizeObserver fails)
    mutationObserver = new MutationObserver(() => postHeightRaf());
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    // [KS] Update after height-related transitions finish (e.g., MUI Accordion/Collapse)
    const handleTransitionEnd = (event: TransitionEvent): void => {
      const property = event.propertyName;
      if (
        property === "height" ||
        property === "max-height" ||
        property === "min-height"
      ) {
        postHeightRaf();
      }
    };
    document.body.addEventListener("transitionend", handleTransitionEnd, true);

    window.addEventListener("resize", postHeightRaf);

    // [KS] Occasionally refresh height value in case we missed an event
    const intervalId = window.setInterval(postHeight, 1000);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
      document.body.removeEventListener(
        "transitionend",
        handleTransitionEnd,
        true,
      );
      window.removeEventListener("resize", postHeightRaf);
      window.clearInterval(intervalId);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);
}

export { useAutoResize };
